import { DndContext, DragOverlay } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import {
    socketEvent,
    taskCreatedMessageSchema,
    taskDeletedMessageSchema,
    taskUpdatedMessageSchema,
} from "@orsane/schema/realtime";
import type { TaskOutputWithNewlyCreated } from "@orsane/schema/tasks";
import { BuiltInAttribute } from "@orsane/schema/tasks";
import { useSearch } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { produce } from "immer";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { toast } from "sonner";
import { ACTION_BAR_HEIGHT, GROUP_HEADER_HEIGHT, SUBTASK_INDENT_WIDTH, TASK_ROW_HEIGHT } from "@/common/constants";
import { useTaskListFetchingStore } from "@/common/stores";
import { useTaskNavigation } from "@/hooks/useTaskNavigation";
import { parseClipboardData } from "@/lib/paste";
import { useSocketMessage } from "@/lib/socket/SocketContext";
import { trpcClient } from "@/lib/trpc";
import { cn, preparePreferenceTaskListAttributes } from "@/lib/utils";
import { useChangeTaskParentMutation, useDeleteTaskMutation } from "@/mutations/task-mutations";
import { useSuspenseDeferredTaskListPreferences } from "@/queries/list-queries";
import { useSuspenseDeferredTasks } from "@/queries/task-queries";
import { AlertDialogType } from "@/types/alert-dialog";
import type { Attribute } from "@/types/attribute";
import { type AlertDialogImperativeHandle, DefaultAlertDialog } from "../DefaultAlertDialog";
import { queryClient, trpc } from "../QueryProvider";
import ScrollContainer from "../ScrollContainer";
import { AddTaskProvider } from "./AddTaskContext";
import { ColumnBuildersProvider } from "./ColumnBuilderContext";
import { NewTaskIndentProvider } from "./NewTaskIndentContext";
import type { ITableRow, Row, RowGroup } from "./RowModel";
import TaskTableGroupHeader from "./TaskListGroupHeader";
import { useTaskQueryParams } from "./TaskQueryParamsContext";
import { TaskSelectionProvider, useTaskSelectionActions, useTaskSelectionStoreApi } from "./TaskSelectionContext";
import TaskTableActionBar from "./TaskTableActionBar";
import TaskTableHeader from "./TaskTableHeader";
import TaskTableTaskRow from "./TaskTableTaskRow";
import { useColumnModel } from "./useColumnModel";
import { useManualSort } from "./useManualSort";
import { type TableModel, useTableModel } from "./useTableModel";
import { useTaskBuilder } from "./useTaskBuilder";

/**
 * Checks if a row is the last visible row in its section (group or table).
 * A row is "last" if the next item in flatRows is a group header or doesn't exist.
 * This accounts for expanded subtask rows — the action bar appears after all of them.
 */
/**
 * Walks backwards through flatRows to find the groupedValue for a subtask row
 * that doesn't have its own group reference.
 */
function findGroupValueFromFlatRows(flatRows: ITableRow[], index: number): string | number | null {
    for (let i = index - 1; i >= 0; i--) {
        const r = flatRows[i];
        if (r.isGroup()) {
            return (r as RowGroup).groupedValue;
        }
        if (!r.isGroup() && (r as Row).group) {
            return (r as Row).group!.groupedValue;
        }
    }
    return null;
}

function isLastVisibleRowInSection(index: number, flatRows: ITableRow[]) {
    const next = flatRows[index + 1];
    return !next || next.isGroup();
}

/**
 * Checks if a row is the first top-level row in its group (or in the table if ungrouped).
 */
function isFirstTopLevelRowInGroup(row: Row, isGrouped: boolean, tableModel: TableModel) {
    if (row.depth > 0) return false;
    if (isGrouped) {
        const group = row.group!;
        const firstTopLevel = group.rows[0];
        return firstTopLevel.key === row.key;
    }
    const firstTopLevel = tableModel.rows[0];
    return firstTopLevel.key === row.key;
}

/**
 * Recursively inserts a row and its expanded subtasks into the flat array.
 */
function insertRowWithSubtasks(row: Row, flatRows: ITableRow[], expandedSubtaskParents: Set<string>) {
    flatRows.push(row);
    if (row.hasSubtasks && expandedSubtaskParents.has(row.key)) {
        for (const child of row.subtaskRows) {
            insertRowWithSubtasks(child, flatRows, expandedSubtaskParents);
        }
    }
}

/**
 * Returns a flat array of rows, including group headers, rows below the groups,
 * and expanded subtask rows nested below their parents.
 */
function calculateFlatRows(
    tableModel: TableModel,
    expanded: Set<string | number>,
    expandedSubtaskParents: Set<string>
) {
    const flatRows: ITableRow[] = [];
    if (tableModel.isGrouped) {
        for (const group of tableModel.rowGroups) {
            flatRows.push(group);
            if (expanded.has(group.groupedValue)) {
                for (const row of group.rows) {
                    insertRowWithSubtasks(row, flatRows, expandedSubtaskParents);
                }
            }
        }
    } else {
        for (const row of tableModel.rows) {
            insertRowWithSubtasks(row, flatRows, expandedSubtaskParents);
        }
    }
    return flatRows;
}

function TasksTableInner({ disabled }: { disabled: boolean }) {
    const queryParams = useTaskQueryParams();
    // We don't want to display the suspend fallback when filtering.
    // https://www.teemutaskula.com/blog/exploring-query-suspense
    const { data: rawTasks, isDeferring: isTasksSuspending } = useSuspenseDeferredTasks(queryParams);
    const deleteTaskAlertDialogRef = useRef<AlertDialogImperativeHandle | null>(null);
    const { preferences } = useSuspenseDeferredTaskListPreferences(queryParams);
    const taskBuilder = useTaskBuilder();
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string | number>>(new Set());
    const [expandedSubtaskParents, setExpandedSubtaskParents] = useState<Set<string>>(new Set());
    // The container reads the selection imperatively and never subscribes to it.
    // Subscribing here meant every selection change re-rendered the whole table
    // (and rebuilt the flat row list); rows now subscribe individually instead.
    const selectionStore = useTaskSelectionStoreApi();
    const { setSelectionByIds, clearSelection, setSelection } = useTaskSelectionActions();
    const getSelectedTaskIds = () => selectionStore.getState().selectedTaskIds;
    // Nothing renders from this, so a ref avoids a re-render per click.
    const lastSelectedTaskIdRef = useRef<number | null>(null);
    const { setFetching } = useTaskListFetchingStore();
    const currentOpenedTaskId = useSearch({ strict: false, select: (s: any) => Number(s.task) || undefined }) as
        | number
        | undefined;
    const { openTask: navigateToTask } = useTaskNavigation();
    const changeParentMutation = useChangeTaskParentMutation();
    const deleteMutation = useDeleteTaskMutation();
    const containerRef = useRef<HTMLDivElement>(null);
    // A subtask table is the one rendered inside a task's detail panel. Nesting is
    // capped at one level, so it can't indent/outdent.
    const isSubtaskTable = !!queryParams.parentTask;

    // When another user creates a task we have access to, fetch it through the active list
    // query (which applies the current URL filters server-side) and merge it into the cache
    // only if it matches. This avoids refetching the whole list, which would discard
    // in-progress edits in other rows.
    useSocketMessage({
        event: socketEvent.task_created,
        schema: taskCreatedMessageSchema,
        callback: async ({ taskId }) => {
            const listKey = trpc.task.list.queryKey(queryParams);
            const cached = queryClient.getQueryData<TaskOutputWithNewlyCreated[]>(listKey);
            if (cached?.some((t) => t.id === taskId)) return;

            // Use trpcClient directly to bypass the React Query cache — fetchQuery would
            // return stale data on repeat lookups of the same taskId.
            const matches = await trpcClient.task.list.query({ ...queryParams, taskIds: [taskId] });
            if (!matches || matches.length === 0) return;

            const current = queryClient.getQueryData<TaskOutputWithNewlyCreated[]>(listKey) ?? [];
            if (current.some((t) => t.id === taskId)) return;
            queryClient.setQueryData(
                listKey,
                produce(current, (draft: TaskOutputWithNewlyCreated[]) => {
                    draft.push(matches[0] as TaskOutputWithNewlyCreated);
                })
            );
        },
    });

    // When a task we have access to is edited, fetch only that task (via the same
    // taskIds-narrowed list query so URL filters are evaluated server-side) and reconcile:
    // replace if present, insert if it just became visible, remove if it no longer matches.
    useSocketMessage({
        event: socketEvent.task_updated,
        schema: taskUpdatedMessageSchema,
        callback: async ({ taskId }) => {
            const listKey = trpc.task.list.queryKey(queryParams);
            // Direct RPC, not fetchQuery — fetchQuery would return cached stale data on
            // repeat updates of the same taskId because of the global staleTime.
            const matches = await trpcClient.task.list.query({ ...queryParams, taskIds: [taskId] });
            const updated = matches?.[0];

            const current = queryClient.getQueryData<TaskOutputWithNewlyCreated[]>(listKey) ?? [];
            const existingIndex = current.findIndex((t) => t.id === taskId);

            if (updated) {
                queryClient.setQueryData(
                    listKey,
                    produce(current, (draft: TaskOutputWithNewlyCreated[]) => {
                        if (existingIndex !== -1) {
                            draft[existingIndex] = updated as TaskOutputWithNewlyCreated;
                        } else {
                            draft.push(updated as TaskOutputWithNewlyCreated);
                        }
                    })
                );
            } else if (existingIndex !== -1) {
                queryClient.setQueryData(
                    listKey,
                    produce(current, (draft: TaskOutputWithNewlyCreated[]) => {
                        draft.splice(existingIndex, 1);
                    })
                );
            }

            queryClient.invalidateQueries({ queryKey: trpc.task.get.queryKey({ id: taskId }) });
        },
    });

    // When a task we have access to is deleted via API/MCP, drop it from any
    // cached list view and invalidate its detail query.
    useSocketMessage({
        event: socketEvent.task_deleted,
        schema: taskDeletedMessageSchema,
        callback: ({ taskId }) => {
            const listKey = trpc.task.list.queryKey(queryParams);
            const current = queryClient.getQueryData<TaskOutputWithNewlyCreated[]>(listKey);
            if (current?.some((t) => t.id === taskId)) {
                queryClient.setQueryData(
                    listKey,
                    produce(current, (draft: TaskOutputWithNewlyCreated[]) => {
                        const idx = draft.findIndex((t) => t.id === taskId);
                        if (idx !== -1) draft.splice(idx, 1);
                    })
                );
            }
            queryClient.invalidateQueries({ queryKey: trpc.task.get.queryKey({ id: taskId }) });
        },
    });

    const groupColumnId = preferences["taskList.grouping"] as string | null;
    const visibleAttributes = preferences["taskList.attributes"] as Attribute[];
    const sortColumnId = (preferences["taskList.sorting"] || "title") as string;
    const sortIsAscending = (preferences["taskList.taskOrder"] || "asc") === "asc";
    const groupSortIsAscending = (preferences["taskList.groupOrder"] || "asc") === "asc";

    const parentRef = useRef<HTMLDivElement>(null);

    const columns = useColumnModel(visibleAttributes, groupColumnId, sortColumnId);

    // Identifies the current column configuration. Part of each virtual item's key,
    // so it must only change when the columns do.
    const columnsKey = useMemo(() => columns.map((col) => col.id).join(","), [columns]);

    // Manual sort hook — owns local state for manualOrder/manualSubtaskOrder.
    // Local state is updated synchronously on drop (no flash), then persisted in background.
    const {
        sensors,
        activeId,
        draggedIds,
        dropIntent,
        overTaskId,
        reorderPosition,
        dropTargetDepth,
        lastNestTargetId,
        handleDragStart,
        handleDragMove,
        handleDragEnd,
        handleDragCancel,
        collisionDetection,
        modifiers,
        manualOrder,
        manualSubtaskOrder,
        setFlatRowsRef,
    } = useManualSort({
        preferences,
        queryParams,
        sortColumnId,
        getSelectedTaskIds,
        disableNesting: isSubtaskTable,
        clearSelection,
    });

    // build the table model — uses local-state order (not preferences) for immediate rendering
    const tableModel = useTableModel(
        columns,
        sortColumnId,
        sortIsAscending,
        groupColumnId,
        groupSortIsAscending,
        rawTasks as TaskOutputWithNewlyCreated[],
        manualOrder,
        manualSubtaskOrder
    );

    // A Set rather than an array: the render loop asks "is this group expanded?"
    // once per visible row, which was a linear scan of every group.
    const expanded = useMemo(() => {
        const expandedValues = new Set<string | number>();
        for (const group of tableModel.rowGroups) {
            if (!collapsedGroups.has(group.groupedValue)) {
                expandedValues.add(group.groupedValue);
            }
        }
        return expandedValues;
    }, [tableModel.rowGroups, collapsedGroups]);

    const handleClearSelection = useCallback(() => {
        clearSelection();
        lastSelectedTaskIdRef.current = null;
    }, [clearSelection]);

    useEffect(() => {
        setFetching(isTasksSuspending);
    }, [isTasksSuspending, setFetching]);

    // Force grabbing cursor on the whole page while dragging
    useEffect(() => {
        if (activeId) {
            document.body.style.cursor = "grabbing";
            return () => {
                document.body.style.cursor = "";
            };
        }
    }, [activeId]);

    useEffect(() => {
        handleClearSelection();
    }, [groupColumnId]);

    // Memoized: this used to run on every render, and because it returned a fresh
    // array each time it also invalidated everything keyed off it (the dnd-kit
    // sortable item list, the virtualizer's inputs).
    const flatRows = useMemo(
        () => calculateFlatRows(tableModel, expanded, expandedSubtaskParents),
        [tableModel, expanded, expandedSubtaskParents]
    );

    // Keep the flatRows ref up to date so drag handlers can access current rows
    setFlatRowsRef(flatRows);

    // Local mirror for the same reason: click/keyboard handlers need the current
    // rows, but taking flatRows as a dependency would give them a new identity on
    // every data change and re-render every row through their props.
    const flatRowsRef = useRef<ITableRow[]>(flatRows);
    flatRowsRef.current = flatRows;

    // Same idea for the task list — read from the cache first, fall back to this.
    const rawTasksRef = useRef(rawTasks);
    rawTasksRef.current = rawTasks;

    // Sortable item IDs for dnd-kit (only task rows, not group headers)
    const sortableItems = useMemo(
        () => flatRows.filter((r) => !r.isGroup() && (r as Row).data.id >= 0).map((r) => (r as Row).data.id.toString()),
        [flatRows]
    );

    const handleToggleSubtasks = useCallback((rowKey: string) => {
        setExpandedSubtaskParents((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }
            return next;
        });
    }, []);

    // Auto-expand parent subtasks after nesting a task via drag-and-drop
    useEffect(() => {
        if (lastNestTargetId !== null) {
            const parentRow = flatRows.find((r) => !r.isGroup() && (r as Row).data.id === lastNestTargetId) as
                | Row
                | undefined;
            if (parentRow) {
                setExpandedSubtaskParents((prev) => new Set(prev).add(parentRow.key));
            }
        }
    }, [lastNestTargetId]);

    const virtualizer = useVirtualizer({
        count: flatRows.length,
        getScrollElement: () => parentRef.current,
        estimateSize: (index) => {
            const row = flatRows[index];
            if (row.isGroup()) {
                return GROUP_HEADER_HEIGHT;
            }
            const isLast = isLastVisibleRowInSection(index, flatRows);
            if (isLast) {
                return ACTION_BAR_HEIGHT + TASK_ROW_HEIGHT;
            }
            return TASK_ROW_HEIGHT;
        },
        overscan: 5,
        // Item keys carry the column configuration so that changing the visible
        // columns remounts rows rather than leaving stale cells behind. The key
        // string is computed once per column change instead of being JSON
        // stringified per row on every render.
        getItemKey: (index) => `${flatRows[index].key}-${columnsKey}`,
    });

    useHotkeys(
        "mod+v",
        async () => {
            // Scope hotkey to this table instance so subtask and main table don't both fire
            if (
                !containerRef.current?.contains(document.activeElement) &&
                document.activeElement !== containerRef.current
            )
                return;
            console.log("PASTE FROM CLIPBOARD");
            const clipboardText = await navigator.clipboard.readText();

            const items = parseClipboardData(clipboardText);

            // Find the current group value, either by the selected row or the first one in the list
            let groupValue = null;
            if (tableModel.isGrouped) {
                const selectedTaskIds = getSelectedTaskIds();
                if (selectedTaskIds) {
                    const selectedTask = flatRows.find(
                        (row) => !row.isGroup() && selectedTaskIds[(row as Row).data.id]
                    );
                    if (selectedTask) {
                        groupValue = (selectedTask as Row).group!.groupedValue;
                    }
                }
                if (!groupValue) {
                    // Single we know the table is grouped, we know the first row in flatRows is a group header, so we can use that
                    groupValue = (flatRows[0] as RowGroup).groupedValue;
                }
            }

            const drafts = [];
            for (const item of items) {
                const newTask = taskBuilder.build(item, groupColumnId, groupValue);
                if (!newTask) return;
                drafts.push(newTask);
            }
            const tasks = await Promise.all(drafts.map((newTask) => trpcClient.task.insert.mutate(newTask)));
            const newTaskIds = tasks.map((task) => task.id);

            // Invalidate and refetch the query instead of manually updating cache
            await queryClient.invalidateQueries({
                queryKey: trpc.task.list.queryKey(queryParams),
            });
            // When pasting in subtasks, also invalidate the main table
            queryParams.onMutationSuccess?.();

            console.log("CLIPBOARD TEXT", items, tasks);
            setSelectionByIds(
                newTaskIds.reduce(
                    (acc, id) => {
                        acc[id] = true;
                        return acc;
                    },
                    {} as Record<number, boolean>
                )
            );

            toast.success("Tasks pasted", {
                description: `${tasks.length} task${tasks.length > 1 ? "s" : ""} pasted into the list.`,
                action: {
                    label: "Undo",
                    onClick: async () => {
                        await Promise.all(newTaskIds.map((id) => trpcClient.task.delete.mutate({ id })));
                        await queryClient.invalidateQueries({ queryKey: trpc.task.list.queryKey(queryParams) });
                        queryParams.onMutationSuccess?.();
                    },
                },
            });
        },
        { preventDefault: true }
    );

    useHotkeys(
        "mod+a",
        () => {
            // Subtask tables only fire when focused inside; the main table also
            // fires when nothing in particular is focused, so the user doesn't
            // need to click the table first.
            const active = document.activeElement;
            const inThisTable =
                !!containerRef.current && (containerRef.current.contains(active) || active === containerRef.current);
            if (isSubtaskTable) {
                if (!inThisTable) return;
            } else {
                const onBody = !active || active === document.body;
                if (!inThisTable && !onBody) return;
            }

            const allIds: Record<number, boolean> = {};
            for (const row of flatRows) {
                if (row.isGroup()) continue;
                const taskRow = row as Row;
                if (taskRow.data.id < 0) continue;
                allIds[taskRow.data.id] = true;
            }
            setSelectionByIds(allIds);
            const ids = Object.keys(allIds);
            lastSelectedTaskIdRef.current = ids.length > 0 ? Number(ids[0]) : null;
        },
        // useKey: match by event.key (actual character) instead of event.code
        // (physical position). Required so the hotkey works on AZERTY/non-US
        // layouts where the physical key labelled "A" produces code "KeyQ".
        { preventDefault: true, useKey: true }
    );

    useHotkeys(
        "backspace, delete",
        async () => {
            // Same scope rules as mod+a: subtask tables only fire when focused
            // inside; main table also fires when focus is on body so the user
            // can hit Delete right after selecting without clicking first.
            const active = document.activeElement;
            const inThisTable =
                !!containerRef.current && (containerRef.current.contains(active) || active === containerRef.current);
            if (isSubtaskTable) {
                if (!inThisTable) return;
            } else {
                const onBody = !active || active === document.body;
                if (!inThisTable && !onBody) return;
            }

            const ids = Object.entries(getSelectedTaskIds())
                .filter(([, v]) => v)
                .map(([id]) => Number(id))
                .filter((id) => id >= 0);
            if (ids.length === 0) return;

            const isBulk = ids.length > 1;
            const singleTaskNumber = !isBulk
                ? (flatRows.find((r) => !r.isGroup() && (r as Row).data.id === ids[0]) as Row | undefined)?.data.number
                : undefined;
            const confirmed = await deleteTaskAlertDialogRef.current?.show(
                isBulk
                    ? {
                          title: `Delete ${ids.length} tasks`,
                          description: `Are you sure you want to delete these ${ids.length} tasks? This action cannot be undone.`,
                      }
                    : singleTaskNumber !== undefined
                      ? {
                            description: `Are you sure you want to delete task T-${singleTaskNumber}? This action cannot be undone.`,
                        }
                      : undefined
            );
            if (!confirmed) return;
            await Promise.all(ids.map((id) => deleteMutation.mutateAsync({ id })));
            handleClearSelection();
        },
        { preventDefault: true }
    );

    useHotkeys(
        "tab",
        () => {
            // Scope hotkey to this table instance so subtask and main table don't both fire
            if (
                !containerRef.current?.contains(document.activeElement) &&
                document.activeElement !== containerRef.current
            )
                return;
            // Subtasks can only be 1 level deep — disable nesting inside a subtask table
            if (isSubtaskTable) return;
            // Make selected tasks subtasks of the previous top-level row in the list
            const selectedIds = Object.entries(getSelectedTaskIds())
                .filter(([, v]) => v)
                .map(([id]) => Number(id));
            if (selectedIds.length === 0) return;

            // Find the first selected task's position in flat rows (top-level only, no groups)
            const topLevelRows = flatRows.filter((r) => !r.isGroup() && (r as Row).depth === 0) as Row[];
            const firstSelectedIndex = topLevelRows.findIndex((r) => selectedIds.includes(r.data.id));
            if (firstSelectedIndex <= 0) return; // No previous row to nest under

            const targetRow = topLevelRows[firstSelectedIndex - 1];
            // Don't nest under a task that is itself selected
            if (selectedIds.includes(targetRow.data.id)) return;

            const parentTask = {
                id: targetRow.data.id,
                number: targetRow.data.number,
                title: targetRow.data.title,
                key: targetRow.data.key,
            };

            for (const id of selectedIds) {
                changeParentMutation.mutate({ id, parentTask });
            }

            // Expand the new parent so subtasks are visible
            setExpandedSubtaskParents((prev) => new Set(prev).add(targetRow.key));

            // Re-focus the container so subsequent hotkeys (e.g. shift+tab) pass the scope check
            // after virtualized rows re-render and unmount the previously focused element.
            containerRef.current?.focus();
        },
        { preventDefault: true }
    );

    useHotkeys(
        "shift+tab",
        () => {
            // Scope hotkey to this table instance so subtask and main table don't both fire
            if (
                !containerRef.current?.contains(document.activeElement) &&
                document.activeElement !== containerRef.current
            )
                return;
            // Subtasks can only be 1 level deep — no un-nesting inside a subtask table
            if (isSubtaskTable) return;
            const selectedIds = Object.entries(getSelectedTaskIds())
                .filter(([, v]) => v)
                .map(([id]) => Number(id));
            if (selectedIds.length === 0) return;

            // Only act on selected tasks that are subtasks (have a parent)
            const subtaskRows = flatRows.filter(
                (r) => !r.isGroup() && selectedIds.includes((r as Row).data.id) && (r as Row).data.parentTask
            ) as Row[];
            if (subtaskRows.length === 0) return;

            for (const row of subtaskRows) {
                changeParentMutation.mutate({ id: row.data.id, parentTask: null });
            }

            // Re-focus the container so subsequent hotkeys pass the scope check
            containerRef.current?.focus();
        },
        { preventDefault: true }
    );

    const handleExpand = useCallback(
        (group: RowGroup) => {
            setCollapsedGroups((prev) => {
                const newCollapsed = new Set(prev);
                if (newCollapsed.has(group.groupedValue)) {
                    newCollapsed.delete(group.groupedValue); // Expand
                } else {
                    newCollapsed.add(group.groupedValue); // Collapse
                }
                return newCollapsed;
            });

            handleClearSelection();
        },
        [handleClearSelection]
    );

    const handleAddTask = useCallback(
        (groupValue: any, parentTask?: TaskOutputWithNewlyCreated["parentTask"]) => {
            const tempTask = taskBuilder.build("", groupColumnId, groupValue);
            if (!tempTask) return;
            handleClearSelection();
            // Keep the nesting of the row the user just finished with Enter, so a run of
            // subtasks only needs one Tab. A subtask table already sets its own parent.
            if (parentTask && !isSubtaskTable) {
                tempTask.parentTask = parentTask;
            }

            // Read fresh data from cache to avoid stale closure overwriting recent updates (e.g. justCreated)
            const queryKey = trpc.task.list.queryKey(queryParams);
            const currentTasks =
                queryClient.getQueryData<TaskOutputWithNewlyCreated[]>(queryKey) ?? rawTasksRef.current;
            const newData = produce(currentTasks, (draft: TaskOutputWithNewlyCreated[]) => {
                draft.push(tempTask);
            });
            queryClient.setQueryData(queryKey, newData);
        },
        [handleClearSelection, taskBuilder, groupColumnId, queryParams, isSubtaskTable]
    );

    // Directly patch a still-unsaved temp task in the cache. It isn't persisted yet, so we
    // can't use changeParentMutation (that would fire a tRPC call with a negative id) — the
    // parent is instead sent along when the row is finally inserted via its title cell.
    const patchTempTaskParent = useCallback(
        (taskId: number, parentTask: TaskOutputWithNewlyCreated["parentTask"]) => {
            const queryKey = trpc.task.list.queryKey(queryParams);
            const currentTasks =
                queryClient.getQueryData<TaskOutputWithNewlyCreated[]>(queryKey) ?? rawTasksRef.current;
            const newData = produce(currentTasks, (draft: TaskOutputWithNewlyCreated[]) => {
                const t = draft.find((task) => task.id === taskId);
                if (t) t.parentTask = parentTask;
            });
            queryClient.setQueryData(queryKey, newData);
        },
        [queryParams]
    );

    // Indent the new task being typed: nest it under the previous top-level row (which,
    // since temp tasks sort last, is the row directly above it). Mirrors the tab hotkey.
    const indentNewTask = useCallback(
        (taskId: number) => {
            // Subtasks can only be 1 level deep — disable nesting inside a subtask table
            if (isSubtaskTable) return;
            const topLevelRows = flatRowsRef.current.filter((r) => !r.isGroup() && (r as Row).depth === 0) as Row[];
            const idx = topLevelRows.findIndex((r) => r.data.id === taskId);
            if (idx <= 0) return; // no previous row to nest under
            const targetRow = topLevelRows[idx - 1];
            // Can't nest under another still-unsaved task (it has no real id yet)
            if (targetRow.data.id < 0) return;
            patchTempTaskParent(taskId, {
                id: targetRow.data.id,
                number: targetRow.data.number,
                title: targetRow.data.title,
                key: targetRow.data.key,
            });
            // Expand the new parent so the nested row stays visible
            setExpandedSubtaskParents((prev) => new Set(prev).add(targetRow.key));
        },
        [isSubtaskTable, patchTempTaskParent]
    );

    // De-indent the new task being typed: back to a top-level row.
    const outdentNewTask = useCallback(
        (taskId: number) => {
            if (isSubtaskTable) return;
            patchTempTaskParent(taskId, null);
        },
        [isSubtaskTable, patchTempTaskParent]
    );

    // Context values for the cells below. Both are read by every TitleCell, and a
    // context value changing identity re-renders consumers regardless of memo — so
    // these have to stay stable across unrelated table renders.
    const newTaskIndentValue = useMemo(
        () => ({ indent: indentNewTask, outdent: outdentNewTask }),
        [indentNewTask, outdentNewTask]
    );

    const { columnDefsString, visibleColumns } = useMemo(() => {
        // Order the columns by the order in the visibleAttributes preferences
        const titleColumn = columns.find((x) => x.id === BuiltInAttribute.title)!;
        const extraColumns = preparePreferenceTaskListAttributes(visibleAttributes).map(
            (attr) => columns.find((x) => x.id === attr.id.toString())!
        );
        const visibleColumns = [titleColumn, ...extraColumns];

        const columnDefsString = visibleColumns
            .filter((x) => x.isVisible)
            .map((x) => x.cssColDef)
            .join(" ");

        return {
            columnDefsString,
            visibleColumns,
        };
    }, [columns, visibleAttributes]);

    // Stable across data changes: every mounted row takes this as a prop, so a new
    // identity here re-renders the whole viewport. It reads the current rows from
    // flatRowsRef instead of closing over flatRows.
    const handleRowClick = useCallback(
        (taskId: number, event: React.MouseEvent) => {
            if (currentOpenedTaskId && !queryParams.parentTaskId) {
                // if the panel is already open, just load the clicked task
                navigateToTask(taskId);
                // and set it selected
                setSelectionByIds({ [taskId]: true });

                return;
            }
            const isMetaClick = event.metaKey || event.ctrlKey; // Cmd on Mac, Ctrl on Windows/Linux
            const isShiftClick = event.shiftKey;

            // Capture before setSelection — event.currentTarget can be reset by
            // the time the zustand setter callback runs.
            const targetEl = event.target as HTMLElement | null;
            const rowEl = event.currentTarget as HTMLElement | null;
            // Click bubbled in from outside the row's DOM (e.g. a popover item
            // rendered in a portal) → treat as a cell editor interaction.
            const isPortalBubble = !!(rowEl && targetEl && !rowEl.contains(targetEl));
            // Click landed on an interactive widget inside the row (popover
            // trigger button etc.).
            const isCellEditorTrigger = !!targetEl?.closest("[data-task-row] button");
            const clickedEditor = isPortalBubble || isCellEditorTrigger;

            setSelection((prev) => {
                let newSelectedTaskIds = { ...prev };

                const lastSelectedTaskId = lastSelectedTaskIdRef.current;
                if (isShiftClick && lastSelectedTaskId !== null) {
                    // Shift+click: select range from last selected to current
                    // Use flatRows to get the actual visible order of tasks in the table
                    const visibleTaskRows = flatRowsRef.current.filter((row) => !row.isGroup()) as Row[];
                    const taskIds = visibleTaskRows.map((row) => row.data.id);
                    const lastIndex = taskIds.indexOf(lastSelectedTaskId);
                    const currentIndex = taskIds.indexOf(taskId);

                    if (lastIndex !== -1 && currentIndex !== -1) {
                        const startIndex = Math.min(lastIndex, currentIndex);
                        const endIndex = Math.max(lastIndex, currentIndex);

                        // Clear previous selection and select range
                        newSelectedTaskIds = {};
                        for (let i = startIndex; i <= endIndex; i++) {
                            newSelectedTaskIds[taskIds[i]] = true;
                        }
                    }
                } else if (isMetaClick) {
                    // Meta+click: toggle selection (multi-select)
                    newSelectedTaskIds[taskId] = !newSelectedTaskIds[taskId];
                    lastSelectedTaskIdRef.current = taskId;
                } else {
                    // A plain click on a row collapses any multi-selection to just
                    // that row, and toggles off a single-row selection. Editor
                    // clicks bypass both rules: the user is interacting with the
                    // cell, not the row, so the current selection (single or
                    // multi) must stay intact for the dispatched edit to apply.
                    const isRowInSelection = !!newSelectedTaskIds[taskId];
                    const selectedCount = Object.values(newSelectedTaskIds).filter(Boolean).length;
                    if (clickedEditor && isRowInSelection) {
                        // editor click on a selected row — keep selection intact
                    } else if (!clickedEditor && isRowInSelection && selectedCount === 1) {
                        // already-selected single row → toggle off
                        newSelectedTaskIds = {};
                        lastSelectedTaskIdRef.current = null;
                    } else {
                        newSelectedTaskIds = { [taskId]: true };
                        lastSelectedTaskIdRef.current = taskId;
                    }
                }

                return newSelectedTaskIds;
            });
        },
        [currentOpenedTaskId, queryParams.parentTaskId, navigateToTask, setSelectionByIds, setSelection]
    );

    const handleCanvasClick = (event: React.MouseEvent) => {
        // Clear the selection when the user clicks anywhere inside the table
        // that isn't a row (header, group header, empty canvas below rows…).
        // Rows mark themselves with [data-task-row] so this check stays
        // dnd-kit-independent.
        const target = event.target as HTMLElement | null;
        const currentTarget = event.currentTarget as HTMLElement | null;
        // Portal-rendered popover items (StatusSelect, UserSelect, …) bubble
        // through the React tree and reach this handler, but their DOM lives
        // under document.body — `closest` would miss the row and we'd wipe the
        // selection right after a bulk edit ran. Skip those.
        if (currentTarget && target && !currentTarget.contains(target)) return;
        if (target?.closest("[data-task-row]")) return;
        handleClearSelection();
    };

    return (
        <NewTaskIndentProvider value={newTaskIndentValue}>
            <AddTaskProvider value={handleAddTask}>
                {/* biome-ignore lint/a11y/noStaticElementInteractions: clearing selection on empty-canvas click */}
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: no keyboard analogue — Esc could also clear but is out of scope */}
                <div
                    ref={containerRef}
                    tabIndex={-1}
                    className="flex-1 relative outline-none"
                    onClick={handleCanvasClick}
                    style={
                        {
                            "--template-columns": columnDefsString,
                        } as React.CSSProperties
                    }
                >
                    <DndContext
                        sensors={sensors}
                        collisionDetection={collisionDetection}
                        modifiers={modifiers}
                        onDragStart={handleDragStart}
                        onDragMove={handleDragMove}
                        onDragEnd={handleDragEnd}
                        onDragCancel={handleDragCancel}
                    >
                        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
                            <ScrollContainer className="absolute h-full w-full overflow-scroll" ref={parentRef}>
                                {/* Fixed Header */}
                                <TaskTableHeader columns={visibleColumns} />
                                {/* Vertical scroll container */}
                                <div
                                    style={
                                        {
                                            minWidth: "100%",
                                            height: `${virtualizer.getTotalSize()}px`,
                                            position: "relative",
                                            "--task-row-height": `${TASK_ROW_HEIGHT}px`,
                                        } as React.CSSProperties
                                    }
                                >
                                    {virtualizer.getVirtualItems().map((virtualRow) => {
                                        const row = flatRows[virtualRow.index];
                                        const isGroup = row.isGroup();

                                        const isLast =
                                            !isGroup && isLastVisibleRowInSection(virtualRow.index, flatRows);
                                        const isFirst =
                                            !isGroup &&
                                            isFirstTopLevelRowInGroup(row as Row, tableModel.isGrouped, tableModel);

                                        // This value is only true if (1) the table has a group defined, (2) we're dealing with a row, and (3) it is expanded.
                                        const isRowExpanded =
                                            tableModel.isGrouped &&
                                            !isGroup &&
                                            (row as Row).depth === 0 &&
                                            expanded.has((row as Row).group!.groupedValue);

                                        // Subtask rows (depth > 0) are always shown when in flatRows (they're only added when parent is expanded)
                                        const showRow = tableModel.isGrouped
                                            ? !isGroup && (isRowExpanded || (row as Row).depth > 0)
                                            : !isGroup;

                                        return (
                                            <div
                                                key={row.key}
                                                data-index={virtualRow.index}
                                                ref={virtualizer.measureElement}
                                                className="min-w-full top-0 absolute will-change-transform z-10"
                                                tabIndex={-1}
                                                style={{
                                                    height: `${virtualRow.size}px`,
                                                    transform: `translateY(${virtualRow.start}px)`,
                                                }}
                                            >
                                                {isGroup && (
                                                    <TaskTableGroupHeader
                                                        tableModel={tableModel}
                                                        rowGroup={row as RowGroup}
                                                        onExpand={handleExpand}
                                                        isExpanded={expanded.has((row as RowGroup).groupedValue)}
                                                    />
                                                )}
                                                {showRow && (
                                                    <TaskTableTaskRow
                                                        deleteTaskAlertDialogRef={deleteTaskAlertDialogRef}
                                                        onRowClick={handleRowClick}
                                                        isFirst={isFirst}
                                                        isLast={isLast}
                                                        row={row as Row}
                                                        columns={visibleColumns}
                                                        onToggleSubtasks={handleToggleSubtasks}
                                                        isSubtasksExpanded={expandedSubtaskParents.has(
                                                            (row as Row).key
                                                        )}
                                                        isNestTarget={
                                                            dropIntent === "nest" && (row as Row).data.id === overTaskId
                                                        }
                                                        dropLinePosition={
                                                            dropIntent === "reorder" &&
                                                            (row as Row).data.id === overTaskId
                                                                ? reorderPosition
                                                                : null
                                                        }
                                                        dropLineDepth={
                                                            dropIntent === "reorder" &&
                                                            (row as Row).data.id === overTaskId
                                                                ? dropTargetDepth
                                                                : undefined
                                                        }
                                                        isDragSource={draggedIds.has((row as Row).data.id)}
                                                    />
                                                )}
                                                {isLast && (
                                                    <TaskTableActionBar
                                                        disabled={disabled}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            const groupedValue = tableModel.isGrouped
                                                                ? ((row as Row).group?.groupedValue ??
                                                                  findGroupValueFromFlatRows(
                                                                      flatRows,
                                                                      virtualRow.index
                                                                  ))
                                                                : null;
                                                            handleAddTask(groupedValue);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                    {flatRows.length === 0 && (
                                        <div>
                                            <div className="text-orsane-muted p-4 ">
                                                There are no tasks in this list yet. Create one by clicking the button
                                                below.
                                            </div>
                                            <TaskTableActionBar
                                                disabled={disabled}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleAddTask(null);
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </ScrollContainer>
                        </SortableContext>
                        <DragOverlay dropAnimation={null}>
                            {activeId != null &&
                                (() => {
                                    const draggedRows = flatRows.filter(
                                        (r) => !r.isGroup() && draggedIds.has((r as Row).data.id)
                                    ) as Row[];
                                    if (draggedRows.length === 0) return null;
                                    return (
                                        <div className="relative pointer-events-none">
                                            {draggedRows.map((row, i) => (
                                                <div
                                                    key={row.key}
                                                    className={cn(
                                                        "grid grid-cols-(--template-columns) bg-white border border-zinc-200 opacity-50",
                                                        i === 0 && "rounded-t-md",
                                                        i === draggedRows.length - 1 && "rounded-b-md",
                                                        i === 0 && "shadow-lg"
                                                    )}
                                                    style={{ height: `${TASK_ROW_HEIGHT}px` }}
                                                >
                                                    {visibleColumns.map((cell, idx) => (
                                                        <div
                                                            key={`overlay-${row.key}-${cell.id}`}
                                                            className={cn(
                                                                "task-cell",
                                                                idx === visibleColumns.length - 1 && "mr-1"
                                                            )}
                                                            style={
                                                                idx === 0 && row.depth > 0
                                                                    ? {
                                                                          paddingLeft: `${row.depth * SUBTASK_INDENT_WIDTH}px`,
                                                                      }
                                                                    : undefined
                                                            }
                                                        >
                                                            {cell.cell({ row })}
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                            {draggedRows.length > 1 && (
                                                <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center shadow opacity-100">
                                                    {draggedRows.length}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                        </DragOverlay>
                    </DndContext>

                    <DefaultAlertDialog
                        ref={deleteTaskAlertDialogRef}
                        type={AlertDialogType.Delete}
                        title="Delete task"
                        description="Are you sure you want to delete this task? This action cannot be undone."
                    />
                </div>
            </AddTaskProvider>
        </NewTaskIndentProvider>
    );
}

// Wrap with ColumnBuildersProvider for stable column builders
export default function TasksTable({ disabled }: { disabled: boolean }) {
    return (
        <TaskSelectionProvider>
            <ColumnBuildersProvider>
                <TasksTableInner disabled={disabled} />
            </ColumnBuildersProvider>
        </TaskSelectionProvider>
    );
}
